import { useRef, useCallback, useEffect } from 'react';
import { ZOOM_MIN, ZOOM_MAX, NODE_WIDTH, NODE_HEIGHT } from '../types';
import { useCanvasStores, useCanvasRotation } from '../stores/CanvasContext';
import type { CanvasPanZoomStore } from '../stores/canvasPanZoomStore';
import { PanDragController } from '../lib/panDragController';

function clampZoom(z: number) {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

function applyZoomAt(panZoomStore: CanvasPanZoomStore, newZoom: number, cursorX: number, cursorY: number) {
  const s = panZoomStore.getSnapshot().context;
  const clamped = clampZoom(newZoom);
  if (clamped === s.zoom) return;
  const ratio = clamped / s.zoom;
  panZoomStore.send({
    type: 'set',
    panX: cursorX - (cursorX - s.panX) * ratio,
    panY: cursorY - (cursorY - s.panY) * ratio,
    zoom: clamped,
  });
}

// ============================================
// Effects (side-effects: global event listeners + rotation compensation)
// ============================================

export function usePanZoomEffects(): void {
  const { containerRef, panZoomStore } = useCanvasStores();
  const rotation = useCanvasRotation();
  const momentumFrameRef = useRef<number | null>(null);

  const getState = useCallback(() => panZoomStore.getSnapshot().context, [panZoomStore]);

  const setState = useCallback((panX: number, panY: number, zoom: number) => {
    panZoomStore.send({ type: 'set', panX, panY, zoom });
  }, [panZoomStore]);

  // Compensate pan when rotation changes so viewport center stays stable
  const prevRotationRef = useRef(rotation);
  useEffect(() => {
    if (prevRotationRef.current === rotation) return;
    const prevRot = prevRotationRef.current;
    prevRotationRef.current = rotation;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const vx = rect.width / 2;
    const vy = rect.height / 2;
    const s = getState();

    const tx = (vx - s.panX) / s.zoom;
    const ty = (vy - s.panY) / s.zoom;
    const oldRad = (-prevRot * Math.PI) / 180;
    const cx = tx * Math.cos(oldRad) - ty * Math.sin(oldRad);
    const cy = tx * Math.sin(oldRad) + ty * Math.cos(oldRad);

    const newRad = (rotation * Math.PI) / 180;
    const rx = cx * Math.cos(newRad) - cy * Math.sin(newRad);
    const ry = cx * Math.sin(newRad) + cy * Math.cos(newRad);

    setState(vx - rx * s.zoom, vy - ry * s.zoom, s.zoom);
  }, [rotation, containerRef, getState, setState]);

  // Wheel: Ctrl/Cmd + scroll = zoom, plain scroll = pan (trackpad two-finger)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      if (e.ctrlKey || e.metaKey) {
        const rect = container.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        const delta = -e.deltaY * 0.008;
        const s = getState();
        applyZoomAt(panZoomStore, s.zoom * (1 + delta), cursorX, cursorY);
      } else {
        if (momentumFrameRef.current !== null) {
          cancelAnimationFrame(momentumFrameRef.current);
          momentumFrameRef.current = null;
        }
        const s = getState();
        setState(s.panX - e.deltaX, s.panY - e.deltaY, s.zoom);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [containerRef, panZoomStore, getState, setState]);

  // Space + drag OR middle mouse pan with momentum
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ctrl = new PanDragController(container, {
      getState,
      setState,
      stopMomentumExternal: () => {
        if (momentumFrameRef.current !== null) {
          cancelAnimationFrame(momentumFrameRef.current);
          momentumFrameRef.current = null;
        }
      },
    });
    ctrl.attach();
    return () => ctrl.detach();
  }, [containerRef, getState, setState]);
}

// ============================================
// Actions (pure callbacks, no side-effects)
// ============================================

export function usePanZoomActions() {
  const { containerRef, panZoomStore } = useCanvasStores();

  const zoomIn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const s = panZoomStore.getSnapshot().context;
    applyZoomAt(panZoomStore, s.zoom * 1.25, rect.width / 2, rect.height / 2);
  }, [containerRef, panZoomStore]);

  const zoomOut = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const s = panZoomStore.getSnapshot().context;
    applyZoomAt(panZoomStore, s.zoom / 1.25, rect.width / 2, rect.height / 2);
  }, [containerRef, panZoomStore]);

  const fitToContent = useCallback((nodePositions: { x: number; y: number }[]) => {
    const container = containerRef.current;
    if (!container || nodePositions.length === 0) return;

    const rect = container.getBoundingClientRect();
    const padding = 80;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodePositions) {
      if (n.x < minX) minX = n.x;
      if (n.y < minY) minY = n.y;
      if (n.x + NODE_WIDTH > maxX) maxX = n.x + NODE_WIDTH;
      if (n.y + NODE_HEIGHT > maxY) maxY = n.y + NODE_HEIGHT;
    }

    const contentW = maxX - minX + padding * 2;
    const contentH = maxY - minY + padding * 2;
    const zoom = clampZoom(Math.min(rect.width / contentW, rect.height / contentH, 2));

    panZoomStore.send({
      type: 'set',
      panX: (rect.width - contentW * zoom) / 2 - (minX - padding) * zoom,
      panY: (rect.height - contentH * zoom) / 2 - (minY - padding) * zoom,
      zoom,
    });
  }, [containerRef, panZoomStore]);

  return { zoomIn, zoomOut, fitToContent };
}
